<!-- NavRail.vue — the 60px mode-colored rail (the "M3" variant of the design
     canvas's drill-collapse/drill-m/nobranch-m3 CSS): a fixed-width rail whose
     background IS the active mode's accent color, with a stek/org switcher up
     top, the active group's sections always listed below a mode tile that
     opens a small "change mode" popup, a hover flyout previewing a section's
     own worktree/pages before you commit to it, and the account menu + the
     sidebar-hide toggle pinned to the bottom. Labels are icon-only at rest
     and fade in on hovering the rail — the rail itself never changes width. -->
<template>
    <nav
        class="nav-rail"
        :class="{
            'nav-rail--expanded': hovering,
            'nav-rail--no-mode-active': noModeActive,
        }"
        :style="{ '--group-accent': `var(${activeGroup.accentVar})` }"
        aria-label="Mode"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
    >
        <div class="nav-rail__slot">
            <button
                ref="orgTriggerRef"
                type="button"
                class="nav-rail__org"
                :title="stekOrOrgName"
                @click="orgPopupOpen = !orgPopupOpen"
            >
                <img
                    v-if="stekLogoVisible"
                    :src="logoDataUrl"
                    class="nav-rail__org-logo"
                    alt="Stek logo"
                />
                <GotokoLogo
                    v-else-if="!organization"
                    class="nav-rail__org-mark"
                />
                <span v-else class="nav-rail__org-ini">{{ orgInitial }}</span>
                <span class="nav-rail__label">{{ stekOrOrgName }}</span>
            </button>
            <Teleport to="body">
                <div
                    v-if="orgPopupOpen"
                    class="nav-rail__scrim"
                    @click="orgPopupOpen = false"
                />
                <div
                    v-if="orgPopupOpen"
                    class="nav-rail__popup"
                    :style="orgPopupStyle"
                >
                    <template v-if="hasMultipleSteks">
                        <div class="nav-rail__popup-label">Switch stek</div>
                        <div
                            v-for="s in steks"
                            :key="s.identity"
                            class="nav-rail__popup-item"
                            :class="{
                                'nav-rail__popup-item--on':
                                    s.name === stek?.name,
                            }"
                            @click="selectStek(s)"
                        >
                            {{ s.name }}
                        </div>
                    </template>
                    <template v-if="hasMultipleOrganizations">
                        <div class="nav-rail__popup-label">
                            Switch organization
                        </div>
                        <div
                            v-for="org in organizations"
                            :key="org.identity"
                            class="nav-rail__popup-item"
                            :class="{
                                'nav-rail__popup-item--on':
                                    org.name === organization?.name,
                            }"
                            @click="selectOrganization(org)"
                        >
                            {{ org.name }}
                        </div>
                    </template>
                    <div
                        v-if="!hasMultipleSteks && !hasMultipleOrganizations"
                        class="nav-rail__popup-empty"
                    >
                        Only one stek here
                    </div>
                </div>
            </Teleport>
        </div>

        <!-- The personal landing page. Stek-scoped like every mode below it,
             but above the mode tile and outside the groups on purpose: it
             belongs to no mode, which is exactly the state
             `nav-rail--no-mode-active` already exists for. Its own slot rather
             than a section row, because the sections list only exists while a
             mode IS active and this row has to survive that. -->
        <div v-if="personalHomeRoute" class="nav-rail__slot">
            <RouterLink
                :to="personalHomeRoute"
                class="nav-rail__section"
                :class="{ 'nav-rail__section--active': onPersonalHome }"
                v-tooltip.right="'What is worth doing next'"
            >
                <i class="pi pi-home" />
                <span class="nav-rail__label">Home</span>
            </RouterLink>
        </div>

        <div class="nav-rail__slot">
            <button
                ref="modeTriggerRef"
                type="button"
                class="nav-rail__mode"
                :aria-expanded="modePopupOpen"
                aria-label="Change mode"
                @click="modePopupOpen = !modePopupOpen"
            >
                <span
                    class="nav-rail__mode-track"
                    :style="{
                        '--group-count': groups.length,
                        '--active-index': activeGroupIndex,
                    }"
                >
                    <span v-if="!noModeActive" class="nav-rail__mode-thumb" />
                    <span
                        v-for="group in groups"
                        :key="group.id"
                        class="nav-rail__mode-track-icon"
                        :class="{
                            'nav-rail__mode-track-icon--active':
                                !noModeActive && group.id === activeGroup.id,
                        }"
                        :style="{ '--item-accent': `var(${group.accentVar})` }"
                    >
                        <component :is="group.icon" />
                    </span>
                </span>
            </button>
            <Teleport to="body">
                <div
                    v-if="modePopupOpen"
                    class="nav-rail__scrim"
                    @click="modePopupOpen = false"
                />
                <div
                    v-if="modePopupOpen"
                    class="nav-rail__popup"
                    :style="modePopupStyle"
                >
                    <div class="nav-rail__popup-label">Change mode</div>
                    <div
                        v-for="group in groups"
                        :key="group.id"
                        class="nav-rail__popup-item nav-rail__popup-item--mode"
                        :class="{
                            'nav-rail__popup-item--on':
                                !noModeActive && group.id === activeGroup.id,
                        }"
                        :style="{ '--item-accent': `var(${group.accentVar})` }"
                        @click="pickGroup(group)"
                    >
                        <component
                            :is="group.icon"
                            class="nav-rail__popup-item-icon"
                        />
                        <span
                            >{{ group.label }}
                            <span class="nav-rail__popup-item-desc">{{
                                MODE_DESC[group.id]
                            }}</span></span
                        >
                    </div>
                </div>
            </Teleport>
        </div>

        <div class="nav-rail__divider" />

        <!-- Rendered before the section rows below (both z-index:auto) so the
             rows paint on top of it — the flyout visually slides out from
             underneath the rail rather than floating above it. Sized against
             .nav-rail itself (not a per-row slot) so it's always full-height,
             regardless of which row triggered it. Width matches the
             persisted panel's current (resizable) width exactly, so nothing
             shifts once a hovered section is committed and the flyout is
             replaced by that same panel. -->
        <div
            v-if="peekedSection && showFlyout"
            class="nav-rail__flyout"
            :style="{ width: `${ui.sidebarWidth}px` }"
            @mouseenter="cancelUnpeek"
            @mouseleave="scheduleUnpeek"
        >
            <NavPanelHeader
                :kicker="activeGroup.label"
                :title="peekedSection.label"
                :tag="peekedSection.tag"
            />
            <div class="nav-rail__flyout-body">
                <nav
                    v-if="peekedSection.key === 'strategy'"
                    class="nav-rail__strategy-preview"
                >
                    <RouterLink
                        v-for="item in strategyPreviewItems"
                        :key="item.name"
                        :to="
                            withThreadRevision({
                                name: item.name,
                                params: strategyLinkParams,
                            })
                        "
                        class="nav-rail__strategy-preview-item"
                    >
                        <i :class="item.icon" />
                        <span>{{ item.label }}</span>
                    </RouterLink>
                </nav>
                <DevNavigation v-else-if="peekedSection.key === 'dev'" />
                <c-stekz-tree
                    v-else-if="stek?.identity"
                    :data-stek-id="stek.identity"
                    :data-selected-domain-id="domain?.identity ?? ''"
                    :data-selected-product-id="product?.identity ?? ''"
                    :data-selected-process-id="process?.identity ?? ''"
                    :data-mode="peekedSection.modeKey"
                ></c-stekz-tree>
            </div>
        </div>

        <!-- Outside any mode (see noModeActive) there's no active group whose
             sections it would make sense to list — jump into a mode via the
             "Change mode" popup above instead. -->
        <div v-if="!noModeActive" class="nav-rail__sections">
            <div
                v-for="section in activeGroup.sections"
                :key="section.key"
                class="nav-rail__slot"
                @mouseenter="peek(section)"
                @mouseleave="scheduleUnpeek"
            >
                <button
                    type="button"
                    class="nav-rail__section"
                    :class="{
                        'nav-rail__section--active':
                            section.key === activeSectionKey,
                        'nav-rail__section--disabled': section.disabled,
                        'nav-rail__section--pointer':
                            section.key === pointerSectionKey,
                    }"
                    v-tooltip.right="section.tooltip"
                    @click="select(section)"
                >
                    <i :class="section.icon" />
                    <span class="nav-rail__label">{{ section.label }}</span>
                </button>
            </div>
        </div>

        <div class="nav-rail__foot">
            <SettingsMenu />
        </div>
    </nav>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import DevNavigation from "@/components/DevNavigation.vue";
import NavPanelHeader from "@/components/NavPanelHeader.vue";
import type {
    Mode,
    ModeGroup,
    ModeGroupSection,
} from "@/composables/mode-navigation";
import {
    BUSINESS_OVERVIEW_ROUTE_NAME,
    STRATEGY_NAV_ITEMS,
    STRATEGY_WATCHERS_ITEM,
} from "@/composables/mode-navigation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import SettingsMenu from "@/layouts/menus/SettingsMenu.vue";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { useCurrentEntityManager } from "@/stores/entity-store";
import type { Organization } from "@/stores/organizations-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import type { Stek } from "@/stores/steks-store";
import { useSteksManager } from "@/stores/steks-store";
import { useUIStore } from "@/stores/ui-store";
import { useUserManager } from "@/stores/user-store";
import "@/wc/stekz-tree/index.js";

import GotokoLogo from "../assets/gotoko-logo-mark.svg?component";

const props = defineProps<{
    groups: ModeGroup[];
    activeMode: Mode;
    activeSectionKey: string;
}>();

const emit = defineEmits<{
    select: [section: ModeGroupSection];
    // Picking a group from the "Change mode" popup while no mode is active
    // (see noModeActive) — there's no current section to preserve context
    // from, so this asks for that mode's landing page directly rather than a
    // specific section (which pickGroup would otherwise default to).
    selectLandingMode: [mode: Mode];
    flyoutOpen: [open: boolean];
}>();

// Design/Build/Run belong to words that don't exist yet as backend modes; the
// design/run descriptions below come straight from the design canvas's own
// copy. Build's names its one page, the design backlog.
const MODE_DESC: Record<string, string> = {
    design: "Model how it should work",
    build: "See what is designed but not built yet",
    run: "Watch it work in production",
};

const activeGroup = computed(
    () =>
        props.groups.find((g) =>
            g.sections.some((s) => s.modeKey === props.activeMode)
        ) ?? props.groups[0]
);
// Drives the mode-track thumb's slide position — which group slot it's
// currently resting on.
const activeGroupIndex = computed(() => {
    const index = props.groups.findIndex((g) => g.id === activeGroup.value.id);
    return index === -1 ? 0 : index;
});
// ApplicationLayout passes "" for activeSectionKey on an outsideMode page
// (Settings, Platform Admin, …) — no row owns the panel slot there because
// the page isn't part of any mode. activeGroup still has to resolve to
// *something* (so the section rows below have something to render), but
// nothing should look like it's the current mode: no accent tint, no track
// thumb, no highlighted group.
const noModeActive = computed(() => props.activeSectionKey === "");

const hovering = ref(false);
const router = useRouter();
// Read-only here — just to size the flyout to match the persisted panel;
// the sidebar-hide toggle itself lives in the panel's own header now.
const ui = useUIStore();

// ── mode switch popup ────────────────────────────────────────
const modePopupOpen = ref(false);
const pickGroup = (group: ModeGroup) => {
    modePopupOpen.value = false;
    // No mode is currently active (see noModeActive) — there's no current
    // section whose context (e.g. which domain/product/process) is worth
    // preserving, so go straight to that mode's landing page rather than
    // defaulting to the group's first section (which for Design is Overview,
    // not its landing page). This bypasses select()'s own disabled guard, so
    // it needs its own — Live being un-entitled (or any other disabled
    // section) must still block navigation here, not just on the row itself.
    if (noModeActive.value) {
        const target = group.sections[0];
        if (!target || target.disabled) return;
        emit("selectLandingMode", target.modeKey);
        return;
    }
    if (group.id === activeGroup.value.id) return;
    const firstSection = group.sections[0];
    if (firstSection) select(firstSection);
};

const select = (section: ModeGroupSection) => {
    if (section.disabled) return;
    modePopupOpen.value = false;
    peekedSection.value = null;
    emit("select", section);
};

// ── stek/org switcher ────────────────────────────────────────
// "Switcher" covers both: switching stek within the current organization
// (the common case) and switching organization — the same two actions
// SettingsMenu's "Switch Stek"/"Switch Organization" items already perform.
const { organization } = useOrganizationManager();
const { organizations } = useOrganizationsManager();
const { stek, logoDataUrl } = useStekManager();
const { steks } = useSteksManager();

const stekLogoVisible = computed(() => {
    const logo = stek.value?.logo;
    if (!logo || !logoDataUrl.value) return false;
    const hasPreview = Boolean(logo.preview);
    const hasLinks = Boolean(logo.links && Object.keys(logo.links).length > 0);
    return hasPreview || hasLinks;
});
const stekOrOrgName = computed(
    () => stek.value?.name ?? organization.value?.name ?? "Gotoko"
);
const orgInitial = computed(() =>
    (stekOrOrgName.value[0] ?? "?").toUpperCase()
);
const hasMultipleSteks = computed(() => (steks.value?.length ?? 0) > 1);
const hasMultipleOrganizations = computed(
    () => (organizations.value?.length ?? 0) > 1
);

const orgTriggerRef = ref<HTMLElement | null>(null);
const modeTriggerRef = ref<HTMLElement | null>(null);
const orgPopupStyle = ref({ left: "0px", top: "0px" });
const modePopupStyle = ref({ left: "0px", top: "0px" });

// Popups are teleported to <body> (see the template) to escape .nav-rail's
// own stacking context (z-index: 602, capped below the top bar's 1000 — see
// that rule's comment), so they can no longer rely on `position: absolute`
// against their trigger's slot. Instead, position them `fixed` from the
// trigger's own viewport rect each time a popup opens.
const positionPopup = (trigger: HTMLElement | null) => {
    if (!trigger) return { left: "0px", top: "0px" };
    const rect = trigger.getBoundingClientRect();
    return { left: `${rect.right}px`, top: `${rect.top}px` };
};

const orgPopupOpen = ref(false);
watch(orgPopupOpen, (open) => {
    if (open) orgPopupStyle.value = positionPopup(orgTriggerRef.value);
});
watch(modePopupOpen, (open) => {
    if (open) modePopupStyle.value = positionPopup(modeTriggerRef.value);
});

const selectStek = (target: Stek) => {
    orgPopupOpen.value = false;
    if (target.name === stek.value?.name) return;
    router.push({
        name: BUSINESS_OVERVIEW_ROUTE_NAME,
        params: {
            orgName: organization.value?.name ?? "",
            stekName: target.name,
        },
    });
};
const selectOrganization = (org: Organization) => {
    orgPopupOpen.value = false;
    if (org.name === organization.value?.name) return;
    router.push({ name: "organizationPage", params: { orgName: org.name } });
};

// ── section hover-peek: preview a section's own tree/pages before
// committing to it, in the same slot its persistent panel occupies ──
const { domain, product, process } = useCurrentEntityManager();
const { watchersEnabled, personalAgentEnabled } = useUserManager();

// The personal landing page's rail row. Gated on the same capability the page
// is: `personal_agent` is what an org has agreed to let gotoko use about a
// person, and a row pointing at a page that would refuse is worse than no row.
//
// It also needs a stek, because the page is about one — which is why this is
// null until org, stek AND capability have all resolved rather than rendering a
// link with a missing param.
const personalHomeRoute = computed(() =>
    personalAgentEnabled.value && organization.value?.name && stek.value?.name
        ? {
              name: "personalHome",
              params: {
                  orgName: organization.value.name,
                  stekName: stek.value.name,
              },
          }
        : null
);

const onPersonalHome = computed(
    () => router.currentRoute.value.name === "personalHome"
);
// Same base list NavSideBar's persisted Strategy nav renders (there, with
// thread-diff badges added on top) — kept in one place so hovering Strategy
// and having committed to it can never show a different set of pages.
const strategyPreviewItems = computed(() => [
    ...STRATEGY_NAV_ITEMS,
    ...(watchersEnabled.value ? [STRATEGY_WATCHERS_ITEM] : []),
]);
const strategyLinkParams = computed(() => ({
    orgName: organization.value?.name,
    stekName: stek.value?.name,
}));
const peekedSection = ref<ModeGroupSection | null>(null);
let unpeekTimer: ReturnType<typeof setTimeout> | null = null;

const clearUnpeekTimer = () => {
    if (unpeekTimer !== null) {
        clearTimeout(unpeekTimer);
        unpeekTimer = null;
    }
};
const peek = (section: ModeGroupSection) => {
    clearUnpeekTimer();
    // Nothing to preview — either this row never has one (see
    // ModeGroupSection.noSubmenu), or there's no stek in context at all (an
    // outsideMode page with no stek, e.g. Platform Admin — see the router's
    // outsideMode meta), which every other row's preview needs (the tree, or
    // the strategy links' own orgName/stekName params). Close any flyout
    // immediately rather than opening an empty one for this row, or leaving a
    // previously-peeked row's flyout lingering underneath it until its own
    // unpeek timer happens to fire.
    if (section.noSubmenu || !stek.value?.identity) {
        peekedSection.value = null;
        return;
    }
    peekedSection.value = section;
};
// Grace period so moving the pointer from the row to the flyout doesn't
// close it — the same technique the (unused) use-hover-popover.ts composable
// uses, adapted locally since this flyout is a full-height docked panel
// rather than a Popover-anchored one.
const scheduleUnpeek = () => {
    clearUnpeekTimer();
    unpeekTimer = setTimeout(() => {
        peekedSection.value = null;
    }, 150);
};
const cancelUnpeek = () => clearUnpeekTimer();

// The persistent panel already shows the active section's content, so a
// second, identical mount of the same tree/nav there would be pure waste.
const showFlyout = computed(
    () => peekedSection.value?.key !== props.activeSectionKey
);
// Whichever section currently "owns" the panel slot to the rail's right —
// the one being peeked, or the active one by default — gets the connector
// triangle pointing at it. Skipped when that section has no panel to point
// at (see ModeGroupSection.noSubmenu, e.g. Overview) — otherwise the active
// row would carry the connector permanently, pointing at nothing.
const pointerSectionKey = computed(() => {
    if (peekedSection.value) return peekedSection.value.key;
    const active = activeGroup.value.sections.find(
        (s) => s.key === props.activeSectionKey
    );
    return active?.noSubmenu ? null : props.activeSectionKey;
});

// ApplicationLayout needs to know when the flyout is occupying the panel
// slot so it can grow the top bar's inset around it — otherwise, with the
// persisted panel collapsed, the top bar would keep covering that slot.
watch(
    () => Boolean(peekedSection.value) && showFlyout.value,
    (open) => emit("flyoutOpen", open),
    { immediate: true }
);
</script>

<style scoped lang="scss">
.nav-rail {
    flex: 0 0 60px;
    width: 60px;
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--group-accent);
    color: #fff;
    position: relative;
    transition: background 0.16s ease-out;
    // Its own stacking context (must clear #sidebar's collapsed z-index:600,
    // stay under the top bar's 1000) so everything inside — including the
    // flyout, which extends visually past the rail's own 60px — stacks
    // relative to each other rather than the whole page.
    z-index: 602;
}
// Outside any mode (see noModeActive) — a fixed neutral tone rather than a
// mode's accent, the same reasoning .nav-rail__mode's own neutral background
// already uses: nothing here should read as "you're in Design/Build/Run".
.nav-rail--no-mode-active {
    background: var(--stekz-black);
}

.nav-rail__slot {
    position: relative;
    // Above .nav-rail__flyout (z-index:1) so a row's own button/popup stay
    // in front of the flyout where its slide-in animation overlaps them.
    z-index: 2;
}

.nav-rail__label {
    display: block;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    line-height: 1.2;
    height: 10px;
    margin-top: 5px;
    opacity: 0;
    overflow: hidden;
    transition: opacity 0.16s ease-out;
}
.nav-rail--expanded .nav-rail__label {
    opacity: 1;
}

.nav-rail__org {
    width: 100%;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    padding: 12px 2px 9px;
    border: none;
    background: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.24);
    cursor: pointer;
    transition: background 0.14s;
    font-family: inherit;

    &:hover {
        background: rgba(0, 0, 0, 0.18);
    }
}
.nav-rail__org-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
}
.nav-rail__org-mark {
    width: 22px;
    height: 22px;
}
.nav-rail__org-ini {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    background: #fff;
    color: var(--group-accent);
    font-size: 11px;
    font-weight: 800;
}

// A fixed neutral anchor rather than a tint of the rail's own (mode-accented)
// background: dark in light mode, light in dark mode — so the switcher reads
// as a control surface independent of whichever mode is currently active.
.nav-rail__mode {
    width: 100%;
    display: grid;
    place-items: center;
    padding: 10px 2px;
    border: none;
    background: var(--stekz-black);
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    font-family: inherit;

    // A solid brightness shift rather than a translucent overlay — the tile's
    // background is fully opaque against the accent-colored rail behind it,
    // and a semi-transparent hover color would let that accent bleed back
    // through. Works for both the light-mode (dark) and dark-mode (light)
    // background below without needing a separate per-theme hover rule.
    &:hover {
        filter: brightness(0.85);
    }
}
// Reaches up to the global `.dark-mode` class the same way DocsPageLayout.vue
// does — the whole selector goes inside :global() since .nav-rail__mode
// doesn't need scoping isolation here (it's a globally unique class name).
:global(html.dark-mode .nav-rail__mode) {
    background: var(--stekz-white);
}
// A pill track with a sliding white thumb, like an iOS-style switch — the
// thumb rests under whichever group's icon is active and slides over on
// mode change, so the control reads as a switch rather than a plain button.
.nav-rail__mode-track {
    position: relative;
    display: flex;
    // Widened (was 44px) so each icon's flex segment has more breathing room
    // — .nav-rail__mode's own horizontal padding was trimmed to match, since
    // 60px-wide rail leaves only so much room either way.
    width: 52px;
    height: 18px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.4);
    overflow: hidden;
}
.nav-rail__mode-thumb {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: calc(100% / var(--group-count, 2));
    background: #fff;
    transform: translateX(calc(100% * var(--active-index, 0)));
    transition: transform 0.16s ease-out;
}
// Each icon carries its own group's accent color at rest — the switch reads as
// a small color key for Design/Build/Run rather than a plain on/off toggle —
// and brightens to full opacity once its thumb slides underneath it.
.nav-rail__mode-track-icon {
    position: relative;
    z-index: 1;
    flex: 1 1 0;
    display: grid;
    place-items: center;
    color: var(--item-accent);
    opacity: 0.7;
    transition: opacity 0.16s ease-out;

    svg {
        width: 10px;
        height: 10px;
    }

    &--active {
        opacity: 1;
    }
}

.nav-rail__divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.24);
    margin: 2px 0 4px;
}

// Fills the space between the divider and the account/settings footer,
// stacking the group's section rows flush under the divider.
.nav-rail__sections {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.nav-rail__section {
    width: 100%;
    display: grid;
    justify-items: center;
    padding: 9px 2px;
    border: none;
    background: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    font-family: inherit;
    text-align: center;
    position: relative;
    // The Home row is a RouterLink rather than a button (it is a destination,
    // not a mode switch), so the shared row style has to survive being an <a>.
    text-decoration: none;

    i {
        font-size: 1.1rem;
    }

    &:hover {
        color: #fff;
        background: rgba(0, 0, 0, 0.16);
    }

    &--active {
        color: #fff;
        background: rgba(0, 0, 0, 0.28);
    }

    &--disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    // The connector triangle: a wedge the color of the panel/flyout it points
    // at, notched into the row so the row and its content visibly fuse.
    &--pointer::after {
        content: "";
        position: absolute;
        right: -1px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 7px solid transparent;
        border-bottom: 7px solid transparent;
        border-right: 7px solid var(--surface-card, #fff);
        z-index: 21;
    }
}
.nav-rail__foot {
    margin-top: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.24);
    display: grid;
    justify-items: center;
    padding: 6px 2px;
    gap: 2px;

    // SettingsMenu's own button is styled for a light top bar; on the colored
    // rail it just needs to render on transparent/inherit.
    :deep(.p-button) {
        background: transparent;
        border-color: rgba(255, 255, 255, 0.4);
        color: #fff;
    }
}
.nav-rail__scrim {
    position: fixed;
    inset: 0;
    // Teleported to <body> (see the template) so it escapes .nav-rail's own
    // stacking context and can sit above the top bar's z-index: 1000.
    z-index: 1100;
}

// Teleported to <body> (see the template) so they escape .nav-rail's own
// stacking context (capped at 602, below the top bar's 1000) — left/top are
// computed from the trigger's own viewport rect (see positionPopup) since
// `position: absolute` against the trigger's slot no longer applies once
// teleported.
.nav-rail__popup {
    position: fixed;
    z-index: 1101;
    width: 226px;
    background: #1b1b1b;
    border: 1px solid #333;
    padding: 6px 0;
}
.nav-rail__popup-label {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
    padding: 6px 14px 8px;
}
.nav-rail__popup-empty {
    padding: 10px 14px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
}
.nav-rail__popup-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.14s,
        color 0.14s;

    &:hover {
        color: #fff;
        background: #262626;
    }

    &--on {
        color: #fff;
        box-shadow: inset 3px 0 0 var(--item-accent, #fff);
    }

    &--mode {
        align-items: flex-start;
    }
}
.nav-rail__popup-item-icon {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-top: 1px;
}
.nav-rail__popup-item-desc {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 2px;
}

// The section-hover preview of that section's own worktree/pages, in the
// same slot the persistent tree panel occupies. z-index:1 (below the rows'
// own stacking context, see .nav-rail__slot) plus the slide-in-from-the-left
// animation makes it read as sliding out from underneath the rail rather
// than floating above it.
.nav-rail__flyout {
    position: absolute;
    left: 100%;
    // Full viewport height, same as #sidebar — its background/border always
    // extend all the way to the top.
    top: 0;
    bottom: 0;
    z-index: 1;
    // width set inline (see the template) to match the persisted panel's
    // current, resizable width — not a fixed value here.
    background: var(--surface-card, #fff);
    // Matches #sidebar's own border-right width exactly, so the flyout's
    // content area (and the header stretched to fill it) is pixel-identical
    // to the persisted panel's at the same ui.sidebarWidth.
    border-right: 2px solid var(--surface-border, #dee2e6);
    display: flex;
    flex-direction: column;
    color: var(--text-color);
    text-align: left;
    animation: nav-rail-flyout-in 0.16s ease-out;
}
@keyframes nav-rail-flyout-in {
    from {
        opacity: 0;
        transform: translateX(-8px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
.nav-rail__flyout-body {
    flex: 1 1 auto;
    overflow-y: auto;
}
.nav-rail__strategy-preview {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
}
.nav-rail__strategy-preview-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    color: var(--text-color);
    text-decoration: none;
    cursor: pointer;

    &:hover {
        background: var(--stekz-primary-background);
    }

    &.router-link-active {
        background: var(--p-highlight-background);
        color: var(--p-primary-color);
        font-weight: 600;
    }
}
</style>
